import { makeAutoObservable, runInAction } from 'mobx';
import httpFetch from '@services/http-fetch-service.ts';
import { mapExperiences } from '@store/experience-store.ts';
import {
	ExperienceTag,
	ExperienceTagListSchema,
	PopulatedExperienceTag,
	PopulatedExperienceTagResponseSchema,
} from '@/schemas/experience-tag-schema.ts';
import authStore from '@store/auth-store.ts';
import { BaseStore } from '@store/base-store.ts';

export const experienceTagApi = `${import.meta.env.VITE_SERVER_URL}/api/placetag`;

class ExperienceTagStore implements BaseStore {
	private _experienceTags: ExperienceTag[] = [];
	private _loadingList = false;
	private _initialized = false;

	private _populatedExperienceTags: Record<number, PopulatedExperienceTag> =
		{};
	private _populatedExperienceTagLoading = false;

	reset() {
		runInAction(() => {
			this._experienceTags = [];
			this._loadingList = false;
			this._initialized = false;
			this._populatedExperienceTags = {};
			this._populatedExperienceTagLoading = false;
		});
	}

	constructor() {
		makeAutoObservable(this);
	}

	async loadExperienceTags() {
		if (!this._loadingList) {
			this._loadingList = true;
			const response = await httpFetch.GET(experienceTagApi, true, true);

			if (response.ok) {
				const experienceTags = ExperienceTagListSchema.parse(
					await response.json()
				);

				runInAction(() => {
					this._experienceTags = experienceTags.sort((a, b) => {
						return a.name.localeCompare(b.name);
					});
					this._initialized = true;
					this._loadingList = false;
				});
			} else {
				runInAction(() => {
					this._initialized = true;
					this._loadingList = false;
				});
			}
		}
	}

	async addExperienceTag(name: string, global: boolean) {
		const response = await httpFetch.POST(experienceTagApi, {
			name,
			global,
		});

		if (response.ok) {
			const newTag = PopulatedExperienceTagResponseSchema.parse(
				await response.json()
			);
			runInAction(() => {
				this._experienceTags.push({
					id: newTag.id,
					name: newTag.name,
					place_ids: newTag.places.map(
						(experience) => experience.place_id
					),
					global,
				});
			});

			return newTag;
		}
	}

	async deleteExperienceTag(id: number) {
		const response = await httpFetch.DELETE(`${experienceTagApi}/${id}`);

		if (response.ok) {
			runInAction(() => {
				this._experienceTags = this._experienceTags.filter(
					(tag) => tag.id !== id
				);
			});
		}

		return response;
	}

	async updateExperienceTag(id: number, update: Partial<ExperienceTag>) {
		const response = await httpFetch.PUT(
			`${experienceTagApi}/${id}`,
			update
		);
		if (response.ok) {
			const updatedTag = PopulatedExperienceTagResponseSchema.parse(
				await response.json()
			);

			runInAction(() => {
				// Add id property to use as a key prop
				this._populatedExperienceTags[updatedTag.id] = {
					id: updatedTag.id,
					name: updatedTag.name,
					places: mapExperiences(updatedTag.places),
					owner_id: updatedTag.owner_id,
					global: updatedTag.global,
				};

				this._populatedExperienceTags[updatedTag.id].places =
					this._populatedExperienceTags[updatedTag.id].places.sort(
						(a, b) => {
							return a.name.localeCompare(b.name);
						}
					);

				this._experienceTags = this._experienceTags.map((tag) => {
					if (tag.id === updatedTag.id) {
						tag.name = updatedTag.name;
						tag.place_ids = updatedTag.places.map(
							(experience) => experience.place_id
						);
					}
					return tag;
				});
			});
		}
	}

	async addExperienceIdToTag(tagId: number, experienceId: number) {
		let experienceTag = this._experienceTags.find(
			(tag) => tag.id === tagId
		);

		if (!experienceTag) {
			const populatedExperienceTag = this._populatedExperienceTags[tagId];
			if (!populatedExperienceTag) {
				return;
			}

			experienceTag = {
				...populatedExperienceTag,
				place_ids: populatedExperienceTag.places.map(
					(experience) => experience.place_id
				),
			};
		}

		experienceTag.place_ids = [...experienceTag.place_ids, experienceId];

		await this.updateExperienceTag(tagId, experienceTag);
	}

	async deleteExperienceIdFromTag(tagId: number, experienceId: number) {
		let experienceTag = this._experienceTags.find(
			(tag) => tag.id === tagId
		);

		if (!experienceTag) {
			const populatedExperienceTag = this._populatedExperienceTags[tagId];
			if (!populatedExperienceTag) {
				return;
			}

			experienceTag = {
				...populatedExperienceTag,
				place_ids: populatedExperienceTag.places.map(
					(experience) => experience.place_id
				),
			};
		}

		experienceTag.place_ids = experienceTag.place_ids.filter(
			(id) => id !== experienceId
		);

		await this.updateExperienceTag(tagId, experienceTag);
	}

	async getPopulatedExperienceTag(id: number) {
		runInAction(() => {
			this._populatedExperienceTagLoading = true;
		});

		const response = await httpFetch.GET(`${experienceTagApi}/${id}`);

		if (response.ok) {
			const tag = PopulatedExperienceTagResponseSchema.parse(
				await response.json()
			);
			runInAction(() => {
				tag.places = tag.places.sort((a, b) => {
					return a.name.localeCompare(b.name);
				});
				// Add id property to use as a key prop
				this._populatedExperienceTags[tag.id] = {
					id: tag.id,
					name: tag.name,
					places: mapExperiences(tag.places),
					owner_id: tag.owner_id,
					global: tag.global,
				};
			});
		}

		runInAction(() => {
			this._populatedExperienceTagLoading = false;
		});
	}

	get experienceTags() {
		return this._experienceTags;
	}

	get globalAndPersonalExperienceTags() {
		const userId = authStore.user?.id ?? 0;

		return this._experienceTags.filter(
			(tag) => tag.global || tag.owner_id === userId
		);
	}

	get globalExperienceTags() {
		return this._experienceTags.filter((tag) => tag.global);
	}

	get isListLoading() {
		return this._loadingList;
	}

	get isPopulatedExperienceTagLoading() {
		return this._populatedExperienceTagLoading;
	}

	populatedExperienceTag(id: number) {
		return this._populatedExperienceTags[id];
	}

	getExperienceTagById(tagId: number) {
		return this._experienceTags.find((tag) => tag.id === tagId);
	}

	getExperienceTagsByExperienceId(experienceId: number) {
		return this._experienceTags.filter((tag) =>
			tag.place_ids.includes(experienceId)
		);
	}

	getGlobalExperienceTagsByExperienceId(experienceId: number) {
		return this._experienceTags.filter(
			(tag) => tag.global && tag.place_ids.includes(experienceId)
		);
	}

	get initialized() {
		return this._initialized;
	}

	async waitForInit(): Promise<void> {
		if (this._initialized) {
			return;
		}

		await new Promise((resolve) => setTimeout(resolve, 250));
		return this.waitForInit();
	}
}

const experienceTagStore = new ExperienceTagStore();
export default experienceTagStore;
